/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { useState } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { cn } from '@/lib/utils';
import {
  faPen,
  faCamera,
  faCalendarStar,
  faSpinnerThird,
} from '@fortawesome/pro-regular-svg-icons';
import type { SizeProp } from '@fortawesome/fontawesome-svg-core';
import { markierungMapper } from '@/utils/markierung';
import { Button } from '@/components/ui/button';
import {
  Variant,
  TerminImage,
} from '@/domain/termine/shared/types/TerminImage';
import { useFileUpload } from '@/domain/termine/hooks/useFileUpload';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { MarkierungType } from '@/domain/termine/shared/types/Markierung';
import { maxFileSizeMB } from '@/domain/termine/shared/utils/fileUploadConfig';

export interface TerminImageProps extends React.ComponentProps<typeof Avatar> {
  image?: TerminImage;
  markierung?: MarkierungType;
  variant: Variant;
  expandable?: boolean;
  onFileSelect?: (file: File | null) => void;
}

export function UploadTerminImage({
  image,
  markierung,
  variant,
  onFileSelect,
  ...avatarProps
}: TerminImageProps) {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);
  const [wasRemoved, setWasRemoved] = useState(false);

  const {
    previewUrl: uploadedImageUrl,
    isDragOver,
    fileInputRef,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleClick,
    handleInputChange,
    handleRemove: baseHandleRemove,
  } = useFileUpload({
    initialFile: image?.file || null,
    onFileSelect: async (file) => {
      if (file) {
        setUploading(true);
        setWasRemoved(false);
        await new Promise((resolve) => setTimeout(resolve, 500));
        setUploading(false);
      }
      onFileSelect?.(file);
    },
  });

  const handleRemove = (e: React.MouseEvent) => {
    setUploading(false);
    setWasRemoved(true);
    baseHandleRemove(e);
  };

  const sizeClasses: Record<string, string> = {
    detail: 'w-16 h-16',
    sheet: 'w-24 h-24',
    default: 'w-6 h-6',
  };
  const sizeClass = variant ? sizeClasses[variant] : sizeClasses.default;

  const iconSizes: Record<string, SizeProp> = {
    detail: '2x',
    sheet: '2x',
    default: 'sm',
  };
  const iconSize: SizeProp = variant ? iconSizes[variant] : iconSizes.default;

  const avatarImageUrl = wasRemoved
    ? null
    : uploadedImageUrl || image?.url || null;
  const imageIsAvailable = avatarImageUrl !== null;

  const rightControls = (
    <div className="flex flex-col items-start gap-4">
      <div className="flex gap-2">
        {imageIsAvailable && (
          <Button
            type="button"
            variant="outline-secondary"
            onClick={handleRemove}
            aria-label="Datei entfernen">
            <span>{t('domain.termine.upload.entfernen')}</span>
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <div>
      <div className="flex items-center gap-4">
        <div
          className={cn('relative flex items-center', sizeClass)}
          onClick={handleClick}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          tabIndex={0}
          role="button"
          aria-label="File upload area"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              handleClick();
            }
          }}>
          <Avatar
            {...avatarProps}
            key={avatarImageUrl || 'fallback'}
            className={cn(
              sizeClass,
              'border-2 border-(--border-color) cursor-pointer',
              avatarProps.className,
              isDragOver ? 'bg-primary-blue-400/5' : '',
            )}
            style={{
              borderColor: markierung ? markierungMapper[markierung].base : '',
              ...avatarProps.style,
            }}>
            {avatarImageUrl ? (
              <AvatarImage
                src={avatarImageUrl}
                alt={image?.name || 'Termin image'}
                className="object-cover"
              />
            ) : (
              <AvatarFallback
                className="bg-transparent flex items-center justify-center"
                title="Datei auswählen oder rein ziehen">
                <FontAwesomeIcon
                  icon={faCalendarStar}
                  size={iconSize}
                  className="text-neutrals-800"
                />
              </AvatarFallback>
            )}
            {uploading && (
              <div
                className={cn(
                  'flex flex-col absolute inset-0',
                  'items-center justify-center z-30 bg-white/80 gap-2',
                )}>
                <FontAwesomeIcon
                  icon={faSpinnerThird}
                  size="2x"
                  spin
                  className="text-primary-blue-600"
                  style={
                    {
                      '--fa-secondary-color': '#D1D1D6',
                    } as React.CSSProperties
                  }
                />
              </div>
            )}
          </Avatar>

          <Button
            type="button"
            variant="circle"
            size="circle"
            className="absolute -bottom-1 -right-1"
            onClick={(e) => {
              e.stopPropagation();
              handleClick();
            }}>
            <FontAwesomeIcon
              icon={uploadedImageUrl ? faPen : faCamera}
              className="text-neutrals-800 w-4 h-3.5"
            />
          </Button>
          <Input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleInputChange}
            tabIndex={-1}
          />
        </div>
        {rightControls}
      </div>
      <div className="pt-4 text-xs text-neutrals-700">
        <span>
          {t(
            variant === 'list'
              ? 'domain.termine.upload.infoMobile'
              : 'domain.termine.upload.infoDesktop',
            { maxFileSize: maxFileSizeMB },
          )}
        </span>
      </div>
    </div>
  );
}
